/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from "lodash/get";
import last from "lodash/last";
import { addAssociatedMetadata, getDefaultStoriesCount } from "./utils";

const getLastCollectionRow = rows => {
  const collectionRows = rows.filter(row => row.type === "collections");
  return collectionRows.length ? last(collectionRows) : {};
};

const updatedStoriesAndCollectionPageData = (rows, collection, customSection, pageConfig) => {
  const initialCollections = new Array(rows.length).fill({});
  const collectionItems = collection.items.filter(collectionItem => collectionItem.type === "collection");
  const storyItems = collection.items.filter(collectionItem => collectionItem.type === "story");
  const updatedStoryItems = [...storyItems];

  const isAdOrWidget = type => ["ads", "widget"].includes(type);
  let currentOffset = 0;
  let collectionIndex = 0;

  const enableIntroCard = get(pageConfig, ["pageSetting", "enableIntroCard"], false);
  const filteredRows = enableIntroCard ? rows : rows.filter(row => row.type !== "intro");
  for (const index in filteredRows) {
    const row = filteredRows[index];
    let { config = {}, layout, rowId: currentRowId, type } = row;
    const { initialLoadCount = "", numberOfStoriesToShow = "" } = config;
    const storiesCount =
      parseInt(initialLoadCount) || parseInt(numberOfStoriesToShow) || getDefaultStoriesCount(layout);

    const getSlotCount = layout => {
      switch (layout) {
        case "ArrowThreeColSixStories":
          return 1;
        case "ArrowTwoColSixStories":
        case "ArrowThreeColSevenStory":
          return 2;
        case "ArrowElevenStories":
        case "ArrowThirteenStories":
          return 5;
        default:
          return 0;
      }
    };

    switch (config.dataType) {
      case "collection": {
        const childCollectionStories = get(collectionItems, [0, "items"], []).filter(item => item.type === "story");
        if (collectionItems?.length) {
          initialCollections[index] = {
            ...collectionItems[0],
            items: childCollectionStories,
            "associated-metadata": row
          };
        }
        break;
      }
      case "collectionWithSlug": {
        const collectionSlugData = customSection?.[config?.slug] || {};
        const getStoryItems = (collectionSlugData.items || []).filter(
          collectionItem => collectionItem.type === "story"
        );

        initialCollections[index] = {
          ...collectionSlugData,
          type: "collection",
          items: getStoryItems,
          "associated-metadata": row
        };
        break;
      }
      default:
        const lastCollectionRow = getLastCollectionRow(filteredRows);
        if (lastCollectionRow.rowId !== currentRowId) {
          config = { ...config, showButton: false };
        }
        if (isAdOrWidget(type)) {
          initialCollections[index] = {
            type: "collection",
            "associated-metadata": row,
            items: []
          };
        } else if (type === "intro") {
          const { name, summary, metadata = {} } = collection || {};
          const {
            "cover-image-s3-key": imageSlug,
            "cover-image-metadata": coverImageMetadata,
            metadata: imageMetadata,
            url: imageUrl
          } = get(metadata, ["cover-image"]) || {};
          initialCollections[index] = {
            type: "collection",
            "associated-metadata": row,
            items: [],
            props: {
              name: name,
              description: summary,
              imageSlug: imageSlug,
              metadata: coverImageMetadata || imageMetadata,
              imageUrl: imageUrl
            }
          };
        } else if (updatedStoryItems.length) {
          currentOffset = currentOffset + storiesCount;
          let updateStoryCount = storiesCount;
          const slotConfig = get(config, ["slotConfig", "0", "type"], "story");

          if (getSlotCount(filteredRows[index].layout) && slotConfig !== "story") {
            updateStoryCount = storiesCount - getSlotCount(filteredRows[index].layout);
            currentOffset = currentOffset - getSlotCount(filteredRows[index].layout);
          }

          const itemsForCurrentRow = updatedStoryItems.splice(0, updateStoryCount);
          initialCollections[index] = {
            ...collection,
            type: "collection",
            items: itemsForCurrentRow,
            "associated-metadata": row,
            currentOffset,
            config
          };
        } else if (!storyItems.length && collectionItems[collectionIndex]) {
          const childCollectionStories = get(collectionItems, [collectionIndex, "items"], []).filter(
            item => item.type === "story"
          );
          initialCollections[index] = {
            ...collectionItems[collectionIndex],
            items: childCollectionStories,
            "associated-metadata": row
          };
          collectionIndex++;
        }
    }
  }
  return { type: "collection", items: initialCollections };
};

export function overrideLayoutsForSection (config, collection, customSection) {
  const pageConfig = get(config, ["pagebuilder-config", "section"]);
  const layouts = get(config, ["pagebuilder-config", "section", "rows"]);
  if (!layouts) {
    return collection;
  }
  try {
    const layoutDataType = get(pageConfig, ["pageSetting", "layoutDataType"], "storiesAndCollections");
    // If section page is set to collection of collections and there are no collections present, we should treat it as collection of stories
    if (
      layoutDataType === "storiesAndCollections" ||
      (layoutDataType === "collections" &&
        collection.items &&
        !collection.items.find(item => item.type === "collection"))
    ) {
      return updatedStoriesAndCollectionPageData(layouts, collection, customSection, pageConfig);
    }
  } catch (error) {
    return collection;
  }
  // layoutDatatype === "collections" and collections exist
  const itemsWithCollection = collection.items.filter(item => item.type === "collection");
  collection.items = itemsWithCollection;
  return addAssociatedMetadata(collection, layouts, config, "section");
}
